import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [resetUrl, setResetUrl] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await axios.post(`/api/auth/forgot-password`, {
        email
      });
      
      setSuccess(response.data.message);
      
      // Show the reset token
      if (response.data.resetToken) {
        setResetToken(response.data.resetToken);
        setResetUrl(response.data.resetUrl || '');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to generate reset code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center"
      style={{
        backgroundImage: 'url(/img/start-background.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        minHeight: '100vh',
        height: '100%'
      }}
    >
      <div className="bg-white/90 backdrop-blur-sm rounded-lg shadow-xl p-8 w-full max-w-md mx-4">
        <h1 className="text-3xl font-bold text-center mb-6">Forgot Password</h1>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            <p>{success}</p>
            {resetToken && (
              <div className="mt-4 p-4 bg-white rounded-lg border-2 border-amber-500">
                <p className="text-sm font-semibold mb-3 text-gray-800">
                  🔑 Password Reset Code:
                </p>
                <p className="text-xs text-gray-600 mb-3">
                  Use this code to reset your password. Click the button below or copy the code.
                </p>
                <div className="bg-gray-100 p-3 rounded border">
                  <p className="text-xs text-gray-600 mb-1">Reset Code (copy this):</p>
                  <p 
                    className="text-sm break-all font-mono bg-white p-2 rounded border cursor-pointer hover:bg-gray-50"
                    onClick={() => {
                      navigator.clipboard.writeText(resetToken);
                      alert('Reset code copied to clipboard!');
                    }}
                    title="Click to copy"
                  >
                    {resetToken}
                  </p>
                </div>
                <div className="mt-3 flex gap-2">
                  <Link 
                    to={`/reset-password?token=${resetToken}`}
                    className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors text-center text-sm"
                  >
                    Reset Password Now
                  </Link>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(resetToken);
                      alert('Reset code copied to clipboard!');
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors text-sm"
                  >
                    Copy Code
                  </button>
                </div>
                <p className="text-xs mt-3 text-gray-600">
                  ⚠️ This code expires in 1 hour. Keep it secure!
                </p>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              placeholder="your@email.com"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter your email address to receive a password reset code.
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md"
          >
            {loading ? 'Generating...' : 'Get Reset Code'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Remember your password?{' '}
            <Link to="/login" className="text-amber-600 hover:text-amber-700 font-semibold">
              Login here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;

